import {
    phpGetPDXPatient,
    phpSearchPDX,
    phpSearchSTM,
    slsGetPDXPatient,
    slsSearchPDX,
} from 'App/services/PdxFindPatientService';
import PESService from 'App/services/PESService';
import convertKeysToCamelCase from 'App/utils/keyConversion/convertKeysToCamelCase';
import removeNullsFromObject from 'App/utils/removeNullsFromObject';
import dayjs from 'dayjs';
import { omit, pick } from 'lodash';

export const searchPDX = (searchParams, isSlsEnabled) => {
    const params = { ...searchParams };
    if (params.birthDate) {
        const date = new Date(params.birthDate);
        params.birthYear = String(date.getFullYear());
        params.birthMonth = String(date.getMonth() + 1).padStart(2, '0');
        params.birthDay = String(date.getUTCDate()).padStart(2, '0');

        delete params.birthDate;
    }
    if (params?.gender?.id) {
        params.gender = params.gender.id.substr(0, 1);
    }
    params.zipCode = params.zip;
    delete params.zip;
    const searchPdx = isSlsEnabled ? slsSearchPDX : phpSearchPDX;
    return searchPdx(omit(params, ['pdxFirst', 'showDisabled'])).then((data) => {
        if (data) {
            return data;
        }
    });
};

export const getPDXPatient = (id, isSlsEnabled) => {
    const getPatient = isSlsEnabled ? slsGetPDXPatient : phpGetPDXPatient;
    return getPatient(id)
        .then((data) => {
            if (id && isSlsEnabled && data?.resultSetSize === 0) {
                return [];
            }
            return Array.isArray(data) ? data : [data];
        })
        .catch((err) => {
            if (err.status === 404) {
                // think of error state to be shown
            }
            return [];
        });
};

export const searchSTM = async (searchParams) => {
    const params = { ...searchParams };

    // By default only search active patients.
    if (searchParams.showDisabled !== true) {
        params.disabledBy = '';
    }
    delete params.showDisabled;
    // Format the birth date
    if (params.birthDate) {
        params.birthDate = dayjs(params.birthDate).format('YYYY/MM/DD');
    }
    // Format the gender
    if (params?.gender?.id) {
        params.gender = params.gender.id;
    }
    const data = await phpSearchSTM(params);
    return data;
};

export const foundDuplicate = async (duplicates, patient) => {
    const result = [];

    for (const stmPatient of duplicates) {
        const [medications, activities] = await Promise.all([
            PESService.getPatientMedications(stmPatient.ID),
            PESService.getActivities(stmPatient.ID),
        ]);

        const updatedPatient = {
            ...stmPatient,
            activityCount: 0,
            medicationCount: 0,
            PDXExternalId: patient.ExternalId,
        };

        if (medications) {
            updatedPatient.medicationCount = Object.keys(medications).length;
        }

        if (activities?.activity) {
            const totalActivities = Object.values(activities.activity).reduce(
                (sum, current) => sum + current.length,
                0
            );
            updatedPatient.activityCount = totalActivities;
        }

        result.push(updatedPatient);
    }

    return result;
};

export const redirectionData = (patient) => {
    const patientData = pick(patient, [
        'FirstName',
        'MiddleName',
        'LastName',
        'BirthDate',
        'Gender',
        'AddressLine1',
        'AddressLine2',
        'City',
        'State',
        'Zip',
        'Country',
        'Ethnicity',
        'MaritalStatus',
        'LanguageSpoken',
        'Email',
        'MobilePhone',
        'HomePhone',
        'ExternalId',
    ]);
    const convertedData = convertKeysToCamelCase(patientData, true);

    return removeNullsFromObject(convertedData);
};

export const findDuplicates = async (patient) => {
    const params = {
        first_name: patient.FirstName,
        last_name: patient.LastName,
        gender: patient.Gender,
        birth_date: dayjs(patient.BirthDate).format('YYYY/MM/DD'),
        disabled_by: '',
    };

    const res = await searchSTM(params);

    return res.length > 0 ? foundDuplicate(res, patient) : false;
};

export const formatZipCode = (input) => {
    if (!input) return input;

    const str = String(input);

    if (str.length === 9) {
        return `${str.slice(0, 5)}-${str.slice(5)}`;
    }

    if (str.length === 5) {
        return str;
    }

    return input;
};
